/**
 * LiveTestingSxCollectField
 *
 * sx_collect, sx_collect_intelligent, sx_collect_associated 타입 질문을 위한 필드 컴포넌트
 * V1 QsInputSxCollector의 V2 버전
 *
 * 기능:
 * - "⊕ symptoms" 버튼 표시 (선택된 증상 수 포함)
 * - 버튼 클릭 시 모달 열림
 * - 모달 내부에서 QsCollapsible 구조로 증상 선택
 * - TreeSx 타입: 체크박스 + ⊕ 버튼 → 클릭 시 증상 상세 모달
 * - 일반 그룹: 아코디언으로 펼치기/접기
 */

import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { difference, uniq } from 'lodash';
import { MdClose } from 'react-icons/md';
import { TbCirclePlus } from 'react-icons/tb';
import { BiPencil } from 'react-icons/bi';
import classNames from 'classnames';
import { useTranslations } from '../../../../../patient/hooks/use-translations';
import { useStaticLocales, type SystemLocaleCodes } from '../../../../../admin/hooks/locales/use-static-locale';
import type { QuestionResponseDto } from '../../../../api/generated/types';
import { translationControllerSearch } from '../../../../api/generated/translation/translation';
import type { V2QsSxCollectIntelligentValue } from '../../../../shared/components/question-types/V2QsSxCollectIntelligent';
import { V2QsCollapsible } from '../../../../shared/components/question-types/V2QsCollapsible';
import { useLiveTestingCollapsible, CollapsibleValue } from './useLiveTestingCollapsible';
import { useAssociatedSymptoms } from './useAssociatedSymptoms';
import LiveTestingSxSelectModal from './LiveTestingSxSelectModal';
import LiveTestingContainer from './LiveTestingContainer';
import { normalizeTranslatedLabelForSurveyLocale } from '../../../../shared/utils/survey-locale-text';
import { getLocaleCode } from '../../../organization/configurations/language/constants';

interface LiveTestingSxCollectFieldProps {
  /** 질문 데이터 */
  question: QuestionResponseDto;
  /** 현재 답변 값 */
  value: V2QsSxCollectIntelligentValue;
  /** 값 변경 핸들러 */
  onChange: (value: V2QsSxCollectIntelligentValue) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
}

function LiveTestingSxCollectField({
  question,
  value,
  onChange,
  disabled = false,
}: LiveTestingSxCollectFieldProps) {
  const { answerKeys, answerOptionsMap, typeOptions, inputType } = question;

  // sx_collect_associated인 경우 모든 아이템을 treeSx로 처리
  const isAssociatedCollector = inputType === 'sx_collect_associated';

  // suppressSxCollectors 옵션 (증상 트리 내 sx_select 질문 숨김)
  // TypeOptionsDto에 정의되지 않은 확장 필드이므로 타입 단언 사용
  const suppressSxCollectors = !!(typeOptions as Record<string, unknown> | undefined)?.suppressSxCollectors;

  // Container에서 surveyId, locale, selectedSymptomTreeKeys 가져오기
  const {
    surveyId,
    currentLocale,
    visitUuid,
    selectedSymptomTreeKeys,
    reportState,
    survey,
    suggestSymptoms,
    isSuggestingSymptoms,
  } = LiveTestingContainer.useContainer();

  // 번역 함수
  const [, { t }] = useTranslations({ locale: currentLocale });
  const forcedLocaleCode = useMemo<SystemLocaleCodes>(
    () => getLocaleCode(currentLocale, 'en') as SystemLocaleCodes,
    [currentLocale]
  );
  const { sltStr } = useStaticLocales({ localeCode: forcedLocaleCode });

  // V1의 alreadyAddedSymptoms 로직 포팅
  // 다른 곳에서 이미 선택된 증상들을 이 질문에서도 표시
  const alreadyAddedSymptoms = useMemo(() => {
    if (!selectedSymptomTreeKeys) return [];
    return selectedSymptomTreeKeys.filter((key) => key && key.startsWith('sx-'));
  }, [selectedSymptomTreeKeys]);
  const selectedTreeKeysForMetadata = useMemo(
    () => (value?.selectedKeys || []).filter((key) => key && key.startsWith('sx-')),
    [value?.selectedKeys]
  );
  const baselineSymptomTreeKeysRef = useRef<string[] | null>(null);
  if (baselineSymptomTreeKeysRef.current === null) {
    baselineSymptomTreeKeysRef.current = uniq([
      ...((selectedSymptomTreeKeys || []).filter((key) => key && key.startsWith('sx-'))),
      ...selectedTreeKeysForMetadata,
    ]);
  }
  const newlyAddedTreeKeysForMetadata = useMemo(
    () =>
      difference(
        selectedTreeKeysForMetadata,
        baselineSymptomTreeKeysRef.current || []
      ),
    [selectedTreeKeysForMetadata]
  );
  const baseUiMetadata = useMemo<Record<string, string>>(
    () => ({ ...(((value?.uiMetadata || {}) as Record<string, string>)) }),
    [value?.uiMetadata]
  );
  const shouldHydrateAssociatedFromCurrentSelection = !baseUiMetadata.hasAssociatedSymptoms;
  const associatedSourceTreeKeys = useMemo(
    () =>
      shouldHydrateAssociatedFromCurrentSelection
        ? selectedTreeKeysForMetadata
        : newlyAddedTreeKeysForMetadata,
    [
      newlyAddedTreeKeysForMetadata,
      selectedTreeKeysForMetadata,
      shouldHydrateAssociatedFromCurrentSelection,
    ]
  );
  const { associatedSymptomKeys } = useAssociatedSymptoms({
    selectedSymptomTreeKeys: associatedSourceTreeKeys,
    surveyId: surveyId ? Number(surveyId) : undefined,
    locale: currentLocale,
    enabled: !!surveyId && associatedSourceTreeKeys.length > 0,
    reportState,
    survey,
  });
  const syncHasSelectedSymptoms = selectedTreeKeysForMetadata.length > 0 ? 'true' : 'false';
  const syncHasAssociatedSymptoms = associatedSymptomKeys.length > 0 ? 'true' : 'false';

  const buildNextValue = (
    nextSelectedKeys: string[],
    nextItemAnswers: Record<string, Record<string, any>>
  ): V2QsSxCollectIntelligentValue => {
    const hasSelectedSymptoms =
      nextSelectedKeys.some((key) => key && key.startsWith('sx-'));

    return {
      selectedKeys: nextSelectedKeys,
      itemAnswers: nextItemAnswers,
      uiMetadata: {
        ...baseUiMetadata,
        hasSelectedSymptoms: hasSelectedSymptoms ? 'true' : 'false',
        // If no symptom tree is selected (e.g. only "none above"), associated symptoms must be false.
        hasAssociatedSymptoms: hasSelectedSymptoms ? syncHasAssociatedSymptoms : 'false',
      },
    };
  };

  const suggestionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (question.inputType !== 'sx_collect_intelligent') return;
    if (!visitUuid) return;
    if (isSuggestingSymptoms) return;
    if (value?.selectedKeys && value.selectedKeys.length > 0) return;

    const requestKey = visitUuid;
    if (suggestionKeyRef.current === requestKey) return;
    suggestionKeyRef.current = requestKey;

    suggestSymptoms(visitUuid)
      .then((response: any) => {
        const symptomKeys =
          response?.symptomKeys || response?.data?.symptomKeys || [];
        if (Array.isArray(symptomKeys) && symptomKeys.length > 0) {
          onChange(buildNextValue(symptomKeys, (value?.itemAnswers as Record<string, Record<string, any>>) || {}));
        }
      })
      .catch(() => {
        // ignore suggestion failures
      });
  }, [
    question.inputType,
    visitUuid,
    isSuggestingSymptoms,
    value?.selectedKeys,
    suggestSymptoms,
    onChange,
    buildNextValue,
    value?.itemAnswers,
  ]);

  // 모달 열림 상태
  const [isMainModalOpen, setIsMainModalOpen] = useState(false);

  // CollapsibleValue로 변환 (useLiveTestingCollapsible이 사용하는 형태)
  // V1처럼 alreadyAddedSymptoms도 포함하여 선택된 것으로 표시
  const mergedSelectedKeys = useMemo(() => {
    const result = uniq([...(value?.selectedKeys || []), ...alreadyAddedSymptoms]);
    console.log('[LiveTestingSxCollectField] mergedSelectedKeys:', {
      valueSelectedKeys: value?.selectedKeys,
      alreadyAddedSymptoms,
      result,
    });
    return result;
  }, [value?.selectedKeys, alreadyAddedSymptoms]);

  // itemAnswers를 value에서 가져오거나 빈 객체 사용
  // 화면 이동 후에도 데이터가 유지되도록 value prop에서 읽어옴
  const valueItemAnswers = (value as any)?.itemAnswers;

  console.log('[LiveTestingSxCollectField] INIT value:', {
    value,
    valueItemAnswers,
    hasItemAnswers: !!valueItemAnswers,
  });

  const [itemAnswers, setItemAnswers] = useState<Record<string, Record<string, any>>>(() => {
    // value에서 itemAnswers 읽기 (확장된 타입으로 저장된 경우)
    const initial = (value as any)?.itemAnswers || {};
    console.log('[LiveTestingSxCollectField] useState initial itemAnswers:', initial);
    return initial;
  });

  // value prop이 변경되면 itemAnswers 동기화 (화면 이동 후 복귀 시)
  useEffect(() => {
    const newItemAnswers = (value as any)?.itemAnswers;
    console.log('[LiveTestingSxCollectField] useEffect sync itemAnswers:', {
      newItemAnswers,
      currentItemAnswers: itemAnswers,
    });
    if (newItemAnswers && Object.keys(newItemAnswers).length > 0) {
      setItemAnswers(newItemAnswers);
    }
  }, [value]);

  const collapsibleValue: CollapsibleValue = useMemo(() => ({
    selectedKeys: mergedSelectedKeys,
    itemAnswers,
  }), [mergedSelectedKeys, itemAnswers]);

  const [selectedSymptomLabelMap, setSelectedSymptomLabelMap] = useState<Record<string, string>>({});
  const fetchedLabelKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!surveyId || !currentLocale) return;

    const keysToFetch = mergedSelectedKeys.filter(
      (key) => key && key.startsWith('sx-') && !selectedSymptomLabelMap[key] && !fetchedLabelKeysRef.current.has(key)
    );
    if (keysToFetch.length === 0) return;

    keysToFetch.forEach((key) => fetchedLabelKeysRef.current.add(key));

    translationControllerSearch({
      surveyId: Number(surveyId),
      keys: keysToFetch,
      locale: currentLocale,
      type: 'patient',
      limit: 1000,
    } as any)
      .then((response: any) => {
        const rows = response?.data || response?.translations || [];
        if (!Array.isArray(rows) || rows.length === 0) return;
        const next: Record<string, string> = {};
        rows.forEach((row: any) => {
          if (row?.key && row?.value) {
            next[row.key] = row.value;
          }
        });
        if (Object.keys(next).length > 0) {
          setSelectedSymptomLabelMap((prev) => ({ ...prev, ...next }));
        }
      })
      .catch(() => {
        // noop - fallback chain will keep existing behavior
      });
  }, [surveyId, currentLocale, mergedSelectedKeys, selectedSymptomLabelMap]);

  // CollapsibleValue 변경을 V2QsSxCollectIntelligentValue로 변환
  const handleCollapsibleChange = (newValue: CollapsibleValue) => {
    console.log('[LiveTestingSxCollectField] handleCollapsibleChange:', {
      newSelectedKeys: newValue.selectedKeys,
      newItemAnswers: newValue.itemAnswers,
    });
    onChange(buildNextValue(newValue.selectedKeys, newValue.itemAnswers || {}));
    // 로컬 상태도 업데이트
    if (newValue.itemAnswers) {
      setItemAnswers(newValue.itemAnswers);
    }
  };

  // collapsible hook 사용
  const {
    groups,
    openGroupKey,
    modalState,
    handleGroupToggle,
    handleItemClick,
    handleItemCheck,
    handleSpecialOptionCheck,
    handleModalClose,
    handleModalSave,
    updateModalAnswer,
  } = useLiveTestingCollapsible({
    answerKeys: answerKeys || [],
    answerOptionsMap: answerOptionsMap || null,
    value: collapsibleValue,
    onChange: handleCollapsibleChange,
    locale: currentLocale,
    surveyId: surveyId ? Number(surveyId) : undefined,
    suppressSxCollectors,
    forceTreeSx: isAssociatedCollector,
  });

  // 현재 선택된 아이템 라벨 (증상 상세 모달용)
  const selectedItemLabel = useMemo(() => {
    if (!modalState.selectedItemKey) return '';

    // 그룹에서 아이템 찾기
    const foundGroup = groups.find((group) => {
      if (group.items) {
        return group.items.some((i) => i.key === modalState.selectedItemKey);
      }
      return group.key === modalState.selectedItemKey;
    });

    if (foundGroup) {
      if (foundGroup.items) {
        const item = foundGroup.items.find((i) => i.key === modalState.selectedItemKey);
        if (item) return item.label;
      }
      return foundGroup.label;
    }

    return modalState.selectedItemKey;
  }, [modalState.selectedItemKey, groups]);

  // 선택된 증상 수
  // 선택된 증상 수 (alreadyAddedSymptoms 포함)
  const selectedCount = mergedSelectedKeys.length;
  const symptomsButtonLabel = useMemo(() => {
    const translated = t('symptoms', null, { fallbackValue: null });
    if (translated && translated !== 'symptoms') {
      return translated;
    }
    return sltStr({ key: 'symptoms' });
  }, [sltStr, t]);

  const humanizeSymptomKey = (key: string) => {
    return key
      .replace(/^sx-ih-/, '')
      .replace(/^sx-ai-/, '')
      .replace(/^sx-/, '')
      .replace(/-/g, ' ');
  };

  // 선택된 증상의 라벨 가져오기
  const getSymptomLabel = (sxKey: string) => {
    const optionData = answerOptionsMap?.[sxKey];
    const translated = t(sxKey, null, { fallbackValue: null });
    const optionLabel = optionData?.label as string | undefined;
    const humanized = humanizeSymptomKey(sxKey);
    const RAW_KEY = /^(q|o|sx|st|stage)-[a-z0-9]/i;
    // If optionLabel is a raw key (sx-*, o-*, etc.), skip it
    const safeOptionLabel = optionLabel && !RAW_KEY.test(optionLabel) ? optionLabel : null;

    // Priority: translation API cache > t() > optionData label > humanized
    const bestLabel = selectedSymptomLabelMap[sxKey]
      || (translated && translated !== sxKey ? (translated as string) : null)
      || safeOptionLabel;

    // If we have a real label, use it directly (no locale normalization needed)
    if (bestLabel && !RAW_KEY.test(bestLabel)) {
      return bestLabel;
    }

    return humanized;
  };

  // 선택된 증상 클릭 (수정 모달 열기)
  const handleSelectedSymptomClick = (sxKey: string) => {
    // groups에서 해당 아이템 찾아서 모달 열기
    handleItemClick(sxKey, sxKey);
  };

  // 증상 삭제 핸들러
  const handleDeleteSymptom = () => {
    const itemKey = modalState.selectedItemKey;
    if (!itemKey) return;

    // selectedKeys에서 제거
    const newSelectedKeys = mergedSelectedKeys.filter((k) => k !== itemKey);

    // itemAnswers에서도 제거
    setItemAnswers((prev) => {
      const updated = { ...prev };
      delete updated[itemKey];
      onChange(buildNextValue(newSelectedKeys, updated));
      return updated;
    });

    // 모달 닫기
    handleModalClose();
  };

  useEffect(() => {
    const currentHasSelectedSymptoms = baseUiMetadata.hasSelectedSymptoms;
    const currentHasAssociatedSymptoms = baseUiMetadata.hasAssociatedSymptoms;

    if (
      currentHasSelectedSymptoms === syncHasSelectedSymptoms &&
      currentHasAssociatedSymptoms === syncHasAssociatedSymptoms
    ) {
      return;
    }

    onChange(buildNextValue(value?.selectedKeys || [], itemAnswers));
  }, [
    baseUiMetadata.hasAssociatedSymptoms,
    baseUiMetadata.hasSelectedSymptoms,
    itemAnswers,
    onChange,
    syncHasAssociatedSymptoms,
    syncHasSelectedSymptoms,
    value?.selectedKeys,
  ]);

  return (
    <div className="flex flex-col gap-3">
      {question.inputType === 'sx_collect_intelligent' &&
        isSuggestingSymptoms &&
        mergedSelectedKeys.length === 0 && (
          <div className="flex items-center justify-center py-3">
            <span className="loading loading-spinner loading-sm text-[#4B7BF5]" />
          </div>
      )}
      {/* 선택된 증상 목록 */}
      {mergedSelectedKeys
        .filter((key) => key !== 'o-none-above') // None above 제외
        .map((sxKey) => (
          <div
            key={sxKey}
            className={classNames(
              'flex items-center justify-between cursor-pointer transition-all duration-200',
              'border-[1.5px] px-4 py-3 rounded-lg',
              'border-[#4B7BF5] bg-primary bg-opacity-5'
            )}
            onClick={() => handleSelectedSymptomClick(sxKey)}
          >
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked
                readOnly
                className="checkbox checkbox-lg checkbox-primary rounded"
              />
              <span className="text-base font-medium text-[#222638]">
                {getSymptomLabel(sxKey)}
              </span>
            </div>
            <BiPencil size={22} className="text-[#4B7BF5]" />
          </div>
        ))}

      {/* ⊕ Symptoms 버튼 */}
      <button
        type="button"
        className="btn btn-outline btn-ghost btn-lg w-full"
        disabled={disabled}
        onClick={() => setIsMainModalOpen(true)}
      >
        <TbCirclePlus size={22} />
        <span className="text-base uppercase">{symptomsButtonLabel}</span>
      </button>

      {/* 정중앙 모달 */}
      {isMainModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* 배경 오버레이 */}
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setIsMainModalOpen(false)}
          />

          {/* 모달 컨텐츠 - 정중앙 */}
          <div className="relative bg-white shadow-xl rounded-xl w-[90%] max-w-md max-h-[80vh] flex flex-col">
            {/* 헤더 */}
            <div className="border-b shrink-0">
              <div className="relative w-full text-center p-3">
                <span className="text-sm font-medium">{sltStr({ key: 'symptoms', capit: true })}</span>
                <button
                  type="button"
                  className="absolute right-2 top-2 btn btn-ghost btn-sm hover:bg-transparent"
                  onClick={() => setIsMainModalOpen(false)}
                >
                  <MdClose size={20} />
                </button>
              </div>
            </div>

            {/* 컨텐츠 - QsCollapsible 구조 */}
            <div className="flex-1 overflow-auto">
              <V2QsCollapsible
                groups={groups}
                openGroupKey={openGroupKey}
                onGroupToggle={handleGroupToggle}
                onItemClick={handleItemClick}
                onItemCheck={handleItemCheck}
                onSpecialOptionCheck={handleSpecialOptionCheck}
                disabled={disabled}
                loadingText={sltStr({ key: 'v2-qs-conditions-loading' })}
                addButtonText={sltStr({ key: 'btn-add', capit: true })}
                editButtonText={sltStr({ key: 'btn-edit', capit: true })}
              />
            </div>

            {/* Done 버튼 (선택이 있을 때만 표시) */}
            {selectedCount > 0 && (
              <div className="border-t p-3 flex justify-center shrink-0">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setIsMainModalOpen(false)}
                >
                  {sltStr({ key: 'btn-done', upper: true })}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 증상 상세 모달 (TreeSx 아이템 클릭 시) */}
      <LiveTestingSxSelectModal
        isOpen={modalState.isOpen}
        symptomName={selectedItemLabel}
        questions={modalState.questions}
        answers={modalState.answers}
        onAnswerChange={updateModalAnswer}
        onSave={handleModalSave}
        onClose={handleModalClose}
        onDelete={modalState.selectedItemKey && mergedSelectedKeys.includes(modalState.selectedItemKey) ? handleDeleteSymptom : undefined}
        isLoading={modalState.isLoading}
        error={modalState.error}
        saveButtonText={sltStr({ key: 'btn-save', capit: true })}
        deleteButtonText={sltStr({ key: 'btn-delete-symptom', title: true })}
      />
    </div>
  );
}

export default memo(LiveTestingSxCollectField);
